// eslint-disable-next-line @typescript-eslint/consistent-type-imports
import React, {
    type ChangeEvent,
    type KeyboardEvent,
    type FocusEvent,
    type FC,
    type MutableRefObject,
    useState,
    HTMLAttributes
} from 'react';
import { DebounceInput } from 'react-debounce-input';
import { ALLOWED_KEY, forceNumber, forcePrice } from './inputHelpers';
import Icon, { type IconString } from '../../2-core-elements/Icon/Icon';
// import React from 'react';

export type ClickOrPressEvent =
        | React.MouseEvent<HTMLButtonElement>
        | React.MouseEvent<HTMLAnchorElement>
        | React.MouseEvent<HTMLDivElement>
        | React.MouseEvent<SVGSVGElement>
        | React.KeyboardEvent<HTMLAnchorElement>
        | React.KeyboardEvent<HTMLDivElement>;

type InputType = 'text' | 'email' | 'password' | 'number' | 'date' | 'search' | 'hidden' | 'checkbox';

export interface InputProps {
    inputRef?: MutableRefObject<HTMLInputElement | null>;
    type?: InputType;
    onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
    onBlur?: (event: FocusEvent<HTMLInputElement>) => void;
    onKeyDown?: (event: KeyboardEvent<HTMLInputElement>) => void;
    onSuffixIconClick?: (event: ClickOrPressEvent) => void;
    onPrefixIconClick?: (event: ClickOrPressEvent) => void;
    placeholder?: string;
    required?: boolean;
    id?: string;
    value?: string | number;
    defaultValue?: string | number;
    textCenter?: boolean;
    inputSize?: 'sm' | 'md' | 'lg';
    min?: number;
    max?: number;
    name?: string;
    forcedKey?: 'number-only' | 'price';
    readOnly?: boolean;
    autoComplete?: string;
    maxLength?: number;
    skin?: 'light' | 'dark';
    tabIndex?: number;
    disabled?: boolean;
    className?: string;
    prefixIcon?: IconString;
    suffixIcon?: IconString;
    fullWidth?: boolean;
    checked?: boolean;
    debounceTimeout?: number;
    inputMode?: HTMLAttributes<HTMLInputElement['inputMode']>;
}

const Input: FC<InputProps> = ({
    inputRef,
    prefixIcon,
    suffixIcon,
    className = '',
    type = 'text',
    placeholder = '',
    onChange,
    onKeyDown,
    onBlur,
    onSuffixIconClick,
    onPrefixIconClick,
    required,
    disabled = false,
    id,
    value,
    defaultValue,
    textCenter,
    inputSize = 'md',
    skin = '1',
    min,
    max,
    name,
    forcedKey,
    readOnly,
    maxLength,
    autoComplete,
    tabIndex,
    fullWidth,
    checked,
    debounceTimeout = 0,
    inputMode,
}) => {
    const [isFocused, setIsFocused] = useState(false);

    const onFinalChange = (event: ChangeEvent<HTMLInputElement>): void => {
        if (onChange) {
            onChange(event);
        }
    };

    const onFinalBlur = (event: FocusEvent<HTMLInputElement>): void => {
        if (onBlur) {
            onBlur(event);
        }
        setIsFocused(false);
    };

    const onFinalFocus = (): void => {
        setIsFocused(true);
    };

    const onFinalKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
        const containAllowedKey = ALLOWED_KEY.includes(e.key);

        if (!containAllowedKey && maxLength && String((e.target as HTMLInputElement).value).length >= maxLength) {
            e.preventDefault();
        }

        if (forcedKey) {
            if (forcedKey === 'number-only') {
                forceNumber(e);
            } else {
                // for price
                forcePrice(e);
            }
        }

        if (onKeyDown) {
            onKeyDown(e);
        }
    };

    const inputIconClass = `${prefixIcon ? 'has-prefix-icon' : ''} ${suffixIcon ? 'has-suffix-icon' : ''}`;
    const textCenterClass = `${textCenter ? 'text-center' : ''}`;
    const inputSizeClass = `input-${inputSize}`;
    const fullWidthClass = `${fullWidth ? 'full-width' : ''}`;
    const skinClass = `skin-${skin}`;
    const focusClass = `${isFocused ? 'is-focused' : ''}`;

    const inputWrapperClass = `input ${className} ${skinClass} ${inputIconClass} ${inputSizeClass} ${textCenterClass} ${fullWidthClass} ${focusClass}`;

    return (
        <div className={inputWrapperClass}>
            {prefixIcon && <Icon onClick={onPrefixIconClick} className="prefix-icon" icon={prefixIcon} />}
            {suffixIcon && <Icon onClick={onSuffixIconClick} className="suffix-icon" icon={suffixIcon} />}
            <DebounceInput
                debounceTimeout={debounceTimeout}
                inputRef={inputRef}
                placeholder={placeholder}
                type={type}
                onChange={onFinalChange}
                onKeyDown={onFinalKeyDown}
                onFocus={onFinalFocus}
                onBlur={onFinalBlur}
                required={required}
                disabled={disabled}
                id={id}
                value={value === null ? '' : value}
                min={min}
                max={max}
                name={name}
                readOnly={readOnly}
                autoComplete={autoComplete}
                spellCheck="false"
                maxLength={maxLength}
                tabIndex={tabIndex}
                checked={checked}
                defaultValue={defaultValue}
                inputMode={inputMode}
            />
        </div>
    );
};

export default Input;
